/**
 * Integration Repository
 */

import { BaseRepository } from './BaseRepository';
import { Integration } from '../types/WorkflowEntities';

class IntegrationRepository extends BaseRepository<Integration> {
    protected entityName = 'integration';
    protected graphQLType = 'Integration';

    // ✅ FERP SAFETY: Mandatory repository pattern method with tenant isolation
    async findByTenantAndId(tenantId: string, id: string): Promise<Integration | null> {
        await this.validateRepositorySafety({
            operation: 'FIND_BY_TENANT_AND_ID',
            domain: 'OPERATIONAL',
            tenantId,
            entityType: 'Integration',
            criticalityLevel: 'HIGH', // Integrations are critical for external connectivity
            metadata: { id, integrationRepository: true }
        });

        const query = `
            query GetIntegrationByTenantAndId($id: String!, $tenant: String!) {
                integration(id: $id, tenant: $tenant) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        try {
            const result = await this.query<{ integration: Integration | null }>(query, {
                id,
                tenant: tenantId
            }, {
                cache: true,
                cacheTime: 3 * 60 * 1000 // 3 minutes cache for integrations
            });

            const integration = result.integration || null;

            // ✅ FERP SAFETY: Log successful operation with integration details
            if (this.auditTrail) {
                await this.auditTrail.logOperationSuccess({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Integration',
                    entityId: id,
                    found: !!integration,
                    integrationType: integration?.type,
                    integrationStatus: integration?.status,
                    integrationEndpoint: integration?.config?.endpoint,
                    timestamp: Date.now()
                });
            }

            return integration;
        } catch (error) {
            // ✅ FERP SAFETY: Log failed operation
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'FIND_BY_TENANT_AND_ID',
                    tenantId,
                    entityType: 'Integration',
                    entityId: id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    // Legacy findById method - redirects to findByTenantAndId
    async findById(integrationId: string): Promise<Integration | null> {
        return this.findByTenantAndId(this.tenant, integrationId);
    }

    // Integration-specific methods

    /**
     * ✅ FERP COMPLIANCE: Chaos ingestion with full audit trail
     * Replaces direct fetch() calls with repository pattern
     */
    async ingestChaosData(request: any): Promise<any> {
        await this.validateRepositorySafety({
            operation: 'CHAOS_INGEST',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Integration',
            criticalityLevel: 'HIGH',
            metadata: { chaosIngestion: true, requestId: request.request_id }
        });

        const mutation = `
            mutation IngestChaosData($input: ChaosIngestInput!) {
                ingestChaosData(input: $input) {
                    success
                    requestId
                    message
                    workflowId
                }
            }
        `;

        try {
            const result = await this.graphQLClient.mutate({
                mutation: this.gql(mutation),
                variables: { input: request },
                context: {
                    headers: { 'x-tenant': this.tenant }
                }
            });

            if (this.auditTrail) {
                await this.auditTrail.logOperation({
                    operation: 'CHAOS_INGEST',
                    tenantId: this.tenant,
                    entityType: 'Integration',
                    entityId: request.request_id,
                    metadata: { success: true },
                    timestamp: Date.now()
                });
            }

            return result.data?.ingestChaosData ?? null;
        } catch (error) {
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'CHAOS_INGEST',
                    tenantId: this.tenant,
                    entityType: 'Integration',
                    entityId: request.request_id,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }

    /**
     * ✅ FERP COMPLIANCE: Get workflow status with audit trail
     */
    async getChaosWorkflowStatus(requestId: string): Promise<any> {
        await this.validateRepositorySafety({
            operation: 'GET_CHAOS_WORKFLOW_STATUS',
            domain: 'OPERATIONAL',
            tenantId: this.tenant,
            entityType: 'Integration',
            criticalityLevel: 'MEDIUM',
            metadata: { requestId, statusCheck: true }
        });

        const query = `
            query GetChaosWorkflowStatus($requestId: String!, $tenant: String!) {
                chaosWorkflowStatus(requestId: $requestId, tenant: $tenant) {
                    status
                    progress
                    results
                    error
                    completed
                }
            }
        `;

        try {
            const result = await this.graphQLClient.query({
                query: this.gql(query),
                variables: { requestId, tenant: this.tenant },
                context: {
                    headers: { 'x-tenant': this.tenant }
                }
            });

            return result.data?.chaosWorkflowStatus ?? null;
        } catch (error) {
            if (this.auditTrail) {
                await this.auditTrail.logOperationFailure({
                    operation: 'GET_CHAOS_WORKFLOW_STATUS',
                    tenantId: this.tenant,
                    entityType: 'Integration',
                    entityId: requestId,
                    error: error instanceof Error ? error.message : String(error),
                    timestamp: Date.now()
                });
            }
            throw error;
        }
    }
    async test(integrationId: string): Promise<any> {
        const mutation = `
            mutation TestIntegration($integrationId: ID!) {
                testIntegration(integrationId: $integrationId) {
                    success
                    responseTime
                    statusCode
                    error
                    timestamp
                }
            }
        `;

        return this.executeQuery(mutation, { integrationId });
    }

    async findByType(type: string): Promise<Integration[]> {
        const query = `
            query FindIntegrationsByType($type: String!) {
                integrations(filter: { type: $type }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { type });
        return result.integrations || [];
    }

    async findActive(): Promise<Integration[]> {
        const query = `
            query FindActiveIntegrations {
                integrations(filter: { status: "active" }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, {});
        return result.integrations || [];
    }

    async execute(integrationId: string, payload: any): Promise<any> {
        const mutation = `
            mutation ExecuteIntegration($integrationId: ID!, $payload: JSON!) {
                executeIntegration(integrationId: $integrationId, payload: $payload) {
                    success
                    response
                    error
                    executionTime
                }
            }
        `;

        return this.executeQuery(mutation, { integrationId, payload });
    }

    protected getFieldSelection(): string {
        return `
            id
            name
            type
            config {
                endpoint
                method
                headers
                authentication {
                    type
                    credentials
                }
                timeout
                retryPolicy {
                    maxAttempts
                    backoffStrategy
                    initialDelay
                }
            }
            status
            lastTested
            testResults {
                success
                responseTime
                statusCode
                error
                timestamp
            }
            createdAt
            updatedAt
        `;
    }
}

export const integrationRepository = new IntegrationRepository();
export { IntegrationRepository };